import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/utils";

const base =
  "group inline-flex items-center justify-center gap-2 rounded-sm px-6 text-sm font-semibold tracking-wide tap-safe motion-craft transition-[background-color,color,box-shadow,border-color] focus-visible:outline-2";

const variants = {
  primary: "bg-forest text-primary-foreground hover:bg-ink shadow-[var(--shadow-lift)]",
  brass: "bg-brass text-accent-foreground hover:bg-walnut hover:text-primary-foreground",
  outline: "border border-ink/25 text-ink hover:border-ink hover:bg-paper",
  ghostLight: "border border-canvas/35 text-canvas hover:bg-canvas/10",
  quiet: "px-0 text-ink underline-offset-4 hover:text-walnut hover:underline",
} as const;

export type CtaVariant = keyof typeof variants;

export function CtaLink({
  to,
  children,
  variant = "primary",
  arrow = false,
  className,
  ...rest
}: {
  to: string;
  children: ReactNode;
  variant?: CtaVariant;
  arrow?: boolean;
  className?: string;
} & Omit<ComponentProps<typeof Link>, "to" | "children" | "className">) {
  return (
    <Link to={to} className={cn(base, variants[variant], className)} {...rest}>
      {children}
      {arrow && (
        <ArrowRight
          aria-hidden="true"
          className="size-4 motion-craft transition-transform group-hover:translate-x-1"
        />
      )}
    </Link>
  );
}

export function CtaButton({
  children,
  variant = "primary",
  arrow = false,
  className,
  ...rest
}: {
  children: ReactNode;
  variant?: CtaVariant;
  arrow?: boolean;
} & ComponentProps<"button">) {
  return (
    <button className={cn(base, variants[variant], className)} {...rest}>
      {children}
      {arrow && (
        <ArrowRight
          aria-hidden="true"
          className="size-4 motion-craft transition-transform group-hover:translate-x-1"
        />
      )}
    </button>
  );
}
